<script lang="ts">
  import DotCarousel from "./DotCarousel.svelte";
  import UserSmallIcon from "../utils/assets/user_small_icon.svelte";
  import FemaleIcon from "../utils/assets/female_icon.svelte";
  import AmenityIcon from "../utils/assets/AmenityIcon.svelte";
  import { AssetLoader } from "../utils/assetLoader";
  import type { ChatMessage } from "../types/globalTypes";
  import { client_config } from "../stores/store";
  import { client_id } from "../stores/chat";
  import {
    BASE_URL,
    clientsWithFloorInfo,
    clientsWithSqftInfo,
    clientsWithAvailableFromOverride,
    clientsWithCampusInfo,
    clientsWithPriceRange,
    roomTypeLabelConfig,
    clientsWithDateReformat,
    featuresLabelConfig,
  } from "../utils/constants";
  export let accentColor = "";
  export let cdnUrl = "";
  export let message: ChatMessage;
  export let showPropertyTranslation: boolean;
  export let id: string;

  $: getDisplayAmenities = (property: any) => {
    if (!property) return [];
    const amenitiesSource = showPropertyTranslation
      ? property?.translated_amenities
      : property?.amenities;
    if (!amenitiesSource?.length) return [];
    return amenitiesSource || [];
  };

  $: formatDateDisplay = (dateStr: string) => {
    if (!clientsWithDateReformat.includes($client_id ?? "")) return dateStr;
    const parts = String(dateStr).split("-");
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  $: getAvailableFromDisplay = (property: any) => {
    const availableFrom = property?.available_from;
    if (
      !availableFrom ||
      !clientsWithAvailableFromOverride.includes($client_id ?? "") ||
      property?.prop_type !== "BTR"
    ) {
      return formatDateDisplay(availableFrom);
    }
    const [day, month, year] = String(availableFrom).split("-").map(Number);
    if (!day || !month || !year) {
      return formatDateDisplay(availableFrom);
    }
    const availableDate = new Date(year, month - 1, day);
    availableDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (availableDate > today) {
      return formatDateDisplay(availableFrom);
    }
    const todayDay = String(today.getDate()).padStart(2, "0");
    const todayMonth = String(today.getMonth() + 1).padStart(2, "0");
    return formatDateDisplay(`${todayDay}-${todayMonth}-${today.getFullYear()}`);
  };

  $: getAvailableTillDisplay = (property: any) => {
    const availableTill = property?.available_till;
    if (
      !availableTill ||
      !clientsWithAvailableFromOverride.includes($client_id ?? "") ||
      property?.prop_type !== "BTR"
    ) {
      return formatDateDisplay(availableTill);
    }
    const [day, month, year] = String(availableTill).split("-").map(Number);
    if (!day || !month || !year) {
      return formatDateDisplay(availableTill);
    }
    const availableDate = new Date(year, month - 1, day);
    availableDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (availableDate > today) {
      return formatDateDisplay(availableTill);
    }
    const todayDay = String(today.getDate()).padStart(2, "0");
    const todayMonth = String(today.getMonth() + 1).padStart(2, "0");
    return formatDateDisplay(`${todayDay}-${todayMonth}-${today.getFullYear()}`);
  };

  $: getRoomTypeLabel = (property: any) => {
    const clientLabel = roomTypeLabelConfig[$client_id ?? ""];
    if (clientLabel) {
      return clientLabel;
    }
    return "Room Type";
  };

  $: getFeaturesLabel = () => {
    return featuresLabelConfig[$client_id ?? ""] || "Features";
  };

  $: getPropertyName = (property: any) => {
    const name = property?.property_name;
    if (typeof name !== "string") return "";
    const trimmed = name.trim();
    const lower = trimmed.toLowerCase();
    if (!trimmed || lower === "undefined" || lower === "null") return "";
    return trimmed;
  };

  // Per-card active tab, keyed by the each-loop index. Defaults to the first
  // tab that has data for that property.
  type TabKey = "overview" | "features";
  let activeTab: Record<number, TabKey> = {};
  const setTab = (index: number, tab: TabKey) => {
    activeTab = { ...activeTab, [index]: tab };
  };

  $: hasBedsRow = (property: any) =>
    ($client_id === "stocktonstudents" && !!property?.bed_count) ||
    ($client_id !== "stocktonstudents" &&
      property?.bed_available &&
      property?.bed_available > 0);

  $: getLevelOfStudyDisplay = (property: any) => {
    const value = property?.level_of_study;
    if (!value) return "";
    const list = Array.isArray(value) ? value : [value];
    return list
      .filter(Boolean)
      .map((v: string) => v.replaceAll("_", " ").toLowerCase())
      .join(", ");
  };

  $: hasOverviewData = (property: any) =>
    !!property?.room_kind ||
    !!property?.unit_id ||
    hasBedsRow(property) ||
    property?.furnished !== null ||
    (property?.floor != null &&
      clientsWithFloorInfo.includes($client_id ?? "") &&
      property?.prop_type === "BTR") ||
    (property?.sqft && clientsWithSqftInfo.includes($client_id ?? "")) ||
    (property?.occupants &&
      property?.occupants > 0 &&
      property?.occupants !== "N/A") ||
    (!!property?.lease && $client_id !== "moda") ||
    !!property?.available_from ||
    !!property?.available_till ||
    !!property?.city ||
    !!property?.utility_fee ||
    !!property?.installments ||
    (clientsWithCampusInfo.includes($client_id ?? "") &&
      (!!property?.catering ||
        property?.distance_to_campus != null ||
        !!getLevelOfStudyDisplay(property)));

  $: hasFeaturesData = (property: any) =>
    getDisplayAmenities?.(property)?.length > 0;

  $: isLsePriceRange = (property: any) =>
    clientsWithPriceRange.includes($client_id ?? "") &&
    property?.price_type === "RANGE" &&
    property?.display_min_price != null &&
    property?.display_max_price != null;

  $: getPriceDisplay = (property: any) => {
    const currency = property?.currency ?? "";
    if (isLsePriceRange(property)) {
      return `${currency}${property?.display_min_price} - ${currency}${property?.display_max_price}`;
    }
    return property?.price != null ? `${currency}${property?.price}` : "-";
  };

  $: hasPriceToShow = (property: any) =>
    property?.price != null || isLsePriceRange(property);

  $: getVisibleTabs = (property: any): TabKey[] => {
    const tabs: TabKey[] = [];
    if (hasOverviewData(property)) tabs.push("overview");
    if (hasFeaturesData(property)) tabs.push("features");
    return tabs;
  };

  $: {
    if (!cdnUrl) {
      const assetLoader = AssetLoader.getInstance();
      cdnUrl = assetLoader.getCDNUrl();
    }
  }

  const formatBillingUnit = (count: any, unit?: string) => {
    const lowerUnit = unit?.toLowerCase() ?? "";
    const value = parseFloat(count);
    if (!lowerUnit || isNaN(value) || value <= 1 || lowerUnit.endsWith("s")) {
      return lowerUnit;
    }
    return `${lowerUnit}s`;
  };

  async function handlePropertyClick(property: any) {
    if (property?.client_switch_enabled) {
      try {
        const response = await fetch(
          `${BASE_URL}/switch_client/${$client_id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              redirected_client_code: property?.client_code,
              lead_id: property?.lead_id,
              property_json: property,
            }),
          },
        );
        const data = await response.json();
        window.open(data.response, "_blank");
      } catch (error) {
        window.open(
          id === "fusiongroup" ? property?.booking_link : property?.source_link,
          "_blank",
        );
      }
    } else {
      window.open(
        id === "fusiongroup" ? property?.booking_link : property?.source_link,
        "_blank",
      );
    }
  }
</script>

<div class="verbaflo-property-carousel-container">
  <DotCarousel>
    {#each message?.property_json as property, i}
      {@const visibleTabs = getVisibleTabs(property)}
      {@const currentTab = activeTab[i] ?? visibleTabs[0] ?? "overview"}
      <div
        class="verbaflo-property-card {message?.property_json?.length === 1
          ? 'single-card'
          : ''}"
      >
        <div class="verbaflo-property-card-image-container">
          <img
            class="verbaflo-property-card-image"
            src={property?.image ||
              `${cdnUrl}/assets/images/property_fallback_image.png`}
            alt={property?.property_name}
          />

          {#if property?.female_only}
            <div class="verbaflo-property-female-only-badge">
              <FemaleIcon />
              <span>Female only</span>
            </div>
          {/if}

          {#if property?.sold_out}
            <div class="verbaflo-property-sold-out-overlay">
              <span class="verbaflo-property-sold-out-text">Sold out</span>
            </div>
          {/if}
          <div class="verbaflo-property-card-title-container">
            {#if getPropertyName(property)}
              <h3 class="verbaflo-property-card-title">
                {getPropertyName(property)}
              </h3>
            {/if}
            {#if property?.room_name}
              <p class="verbaflo-property-card-room-name-title">
                {property?.room_name}
                {property?.client_name ? `- ${property?.client_name}` : ""}
              </p>
            {/if}
          </div>
        </div>
        <div class="verbaflo-property-card-details">
          <div class="verbaflo-property-tabbed-content">
            {#if visibleTabs.length > 1}
              <div class="verbaflo-property-tab-bar" role="tablist">
                {#if visibleTabs.includes("overview")}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={currentTab === "overview"}
                    class="verbaflo-property-tab {currentTab === 'overview'
                      ? 'active'
                      : ''}"
                    on:click={() => setTab(i, "overview")}
                  >
                    Overview
                  </button>
                {/if}
                {#if visibleTabs.includes("features")}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={currentTab === "features"}
                    class="verbaflo-property-tab {currentTab === 'features'
                      ? 'active'
                      : ''}"
                    on:click={() => setTab(i, "features")}
                  >
                    {getFeaturesLabel()}
                  </button>
                {/if}
              </div>
            {/if}

            <div class="verbaflo-property-tab-panel">
              {#if currentTab === "overview"}
                {#if property?.room_kind}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label"
                      >{getRoomTypeLabel(property)}:</span
                    >
                    <p class="verbaflo-property-value">
                      {property?.room_kind.replaceAll("_", " ").toLowerCase() ||
                        "-"}
                    </p>
                  </div>
                {/if}
                {#if property?.unit_id}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Unit:</span>
                    <p
                      class="verbaflo-property-value verbaflo-property-value-raw"
                    >
                      {property?.unit_id}
                    </p>
                  </div>
                {/if}
                {#if property?.bed_count && $client_id === "stocktonstudents"}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">No. of Beds:</span>
                    <p class="verbaflo-property-value">
                      {property?.bed_count ? `${property?.bed_count}` : "-"}
                    </p>
                  </div>
                {/if}
                {#if property?.bed_available && property?.bed_available > 0 && $client_id !== "stocktonstudents"}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">No. of Beds:</span>
                    <p class="verbaflo-property-value">
                      {property?.bed_available
                        ? `${property?.bed_available}`
                        : "-"}
                    </p>
                  </div>
                {/if}
                {#if property?.furnished !== null}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Furnished:</span>
                    <p class="verbaflo-property-value">
                      {property?.furnished ? "Yes" : "No"}
                    </p>
                  </div>
                {/if}
                {#if property?.floor != null && clientsWithFloorInfo.includes($client_id ?? "") && property?.prop_type === "BTR"}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Floor:</span>
                    <p class="verbaflo-property-value">
                      {property?.floor == 0 ? "Ground" : property?.floor}
                    </p>
                  </div>
                {/if}
                {#if property?.sqft && clientsWithSqftInfo.includes($client_id ?? "")}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Area:</span>
                    <p class="verbaflo-property-value">
                      {property?.sqft}
                      <span class="verbaflo-property-value-unit">sqft</span>
                    </p>
                  </div>
                {/if}
                {#if property?.occupants && property?.occupants > 0 && property?.occupants !== "N/A"}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Occupants:</span>
                    <div class="verbaflo-property-card-occupants-container">
                      <UserSmallIcon />
                      <p class="verbaflo-property-card-occupants-count">
                        {property?.occupants || "-"}
                      </p>
                    </div>
                  </div>
                {/if}
                {#if property?.lease && $client_id !== "moda"}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Lease:</span>
                    <p class="verbaflo-property-value">
                      {property?.lease
                        ? `${property?.lease} ${formatBillingUnit(property?.lease, property?.billing_unit)}`
                        : "-"}
                    </p>
                  </div>
                {/if}
                {#if property?.city}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">City:</span>
                    <p class="verbaflo-property-value">{property?.city}</p>
                  </div>
                {/if}
                {#if property?.available_from}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Available From:</span>
                    <p class="verbaflo-property-value">
                      {getAvailableFromDisplay(property)}
                    </p>
                  </div>
                {/if}
                {#if property?.available_till}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Available Till:</span>
                    <p class="verbaflo-property-value">
                      {getAvailableTillDisplay(property)}
                    </p>
                  </div>
                {/if}
                {#if clientsWithCampusInfo.includes($client_id ?? "") && property?.catering}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Catering:</span>
                    <p class="verbaflo-property-value">
                      {property?.catering.replaceAll("_", " ").toLowerCase()}
                    </p>
                  </div>
                {/if}
                {#if clientsWithCampusInfo.includes($client_id ?? "") && property?.distance_to_campus != null}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Campus Distance:</span>
                    <p class="verbaflo-property-value">
                      {property?.distance_to_campus}
                      <span class="verbaflo-property-value-unit">km</span>
                    </p>
                  </div>
                {/if}
                {#if clientsWithCampusInfo.includes($client_id ?? "") && getLevelOfStudyDisplay(property)}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Level of Study:</span>
                    <p class="verbaflo-property-value">
                      {getLevelOfStudyDisplay(property)}
                    </p>
                  </div>
                {/if}
                {#if property?.utility_fee}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">Utility fee:</span>
                    <p class="verbaflo-property-value">
                      {`${property?.currency}${property?.utility_fee}` || "-"}
                    </p>
                  </div>
                {/if}
                {#if property?.installments}
                  <div class="verbaflo-property-data-row">
                    <span class="verbaflo-property-label">EMI:</span>
                    <p class="verbaflo-property-value">
                      {property?.installments
                        ? `${property?.installments} ${property?.installments === 1 ? "installment" : "installments"}`
                        : "-"}
                    </p>
                  </div>
                {/if}
              {:else if currentTab === "features"}
                <div class="verbaflo-property-feature-column">
                  {#each getDisplayAmenities(property) as amenity}
                    <div class="verbaflo-property-feature-cell">
                      <AmenityIcon name={amenity} />
                      <span
                        class="verbaflo-property-feature-label"
                        title={amenity}
                      >
                        {amenity}
                      </span>
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          </div>

          <div class="verbaflo-property-card-bottom-container">
            {#if hasPriceToShow(property)}
              <div>
                <p class="verbaflo-property-price-value">
                  {getPriceDisplay(property)}
                  <span class="verbaflo-property-week-label">
                    {property?.billing_unit
                      ? `/${property?.billing_unit?.toLowerCase()}`
                      : ""}
                  </span>
                </p>
              </div>
            {/if}
            <button
              style="background-color: {accentColor} !important;"
              class="verbaflo-property-card-button"
              on:click={() => handlePropertyClick(property)}
            >
              {property?.cta_name || "Visit Property"}
            </button>
          </div>
        </div>
      </div>
    {/each}
  </DotCarousel>
</div>

<style>
  :global(#vfwidget .verbaflo-property-message-container) {
    background-color: blue !important;
    border-radius: 8px !important;
    border-bottom-left-radius: 0 !important;
    padding: 10px !important;
    max-width: 280px !important;
  }

  :global(#vfwidget .verbaflo-property-message-container *) {
    font-size: 14px !important;
    margin: 0px !important;
    color: black !important;
  }

  :global(#vfwidget .verbaflo-property-message-text) {
    font-size: 14px !important;
    padding: 8px 0 !important;
  }

  :global(#vfwidget .verbaflo-property-carousel-container) {
    margin-top: 4px !important;
    background-color: #f6f6f6 !important;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.1),
      0 1px 2px rgba(0, 0, 0, 0.06) !important;
    border-radius: 8px !important;
    padding: 10px !important;
    max-width: 100% !important;
    overflow: hidden !important;
    position: relative !important;
    isolation: isolate !important;
    transform: translateZ(0) !important;
    backface-visibility: hidden !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-card) {
    background-color: #ffffff !important;
    width: 100% !important;
    flex-shrink: 0 !important;
    max-width: 80% !important;
    display: flex !important;
    flex-direction: column !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 8px !important;
    user-select: none !important;
    transform: translateZ(0) !important;
  }

  :global(#vfwidget .verbaflo-property-card.single-card) {
    max-width: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-card-image-container) {
    height: 150px !important;
    width: 100% !important;
    overflow: hidden !important;
    flex-shrink: 0 !important;
    position: relative !important;
  }

  :global(#vfwidget .verbaflo-property-card-image-container::after) {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 33.97%,
      rgba(0, 0, 0, 0.7) 100%
    ) !important;
    pointer-events: none !important;
  }

  :global(#vfwidget .verbaflo-property-card-image) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-top-left-radius: 8px !important;
    border-top-right-radius: 8px !important;
  }

  :global(#vfwidget .verbaflo-property-sold-out-overlay) {
    position: absolute !important;
    inset: 0 !important;
    background-color: rgba(255, 255, 255, 0.6) !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    z-index: 20 !important;
  }

  :global(#vfwidget .verbaflo-property-sold-out-text) {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #2c2c2c !important;
    background-color: #ffffff !important;
    padding: 4px 10px !important;
    border-radius: 4px !important;
    z-index: 10 !important;
  }

  :global(#vfwidget .verbaflo-property-female-only-badge) {
    position: absolute !important;
    top: 8px !important;
    right: 8px !important;
    z-index: 10 !important;
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
    background-color: #de6c83 !important;
    color: #ffffff !important;
    font-size: 10px !important;
    font-weight: 500 !important;
    padding: 4px 8px !important;
    border-radius: 4px !important;
  }

  :global(#vfwidget .verbaflo-property-card-occupants-container) {
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
    background-color: #ededed !important;
    padding: 4px !important;
    border-radius: 4px !important;
  }

  :global(#vfwidget .verbaflo-property-card-occupants-count) {
    font-weight: 500 !important;
    font-size: 10px !important;
    color: #2c2c2c !important;
  }

  :global(#vfwidget .verbaflo-property-card-details) {
    padding: 10px 12px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
    font-size: 11px !important;
    height: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-tabbed-content) {
    display: flex !important;
    flex-direction: column !important;
    gap: 10px !important;
    height: 217px !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-tab-bar) {
    display: flex !important;
    align-items: center !important;
    gap: 2px !important;
    flex-shrink: 0 !important;
    width: 100% !important;
    padding: 2px !important;
    background-color: #f4f4f4 !important;
    border-radius: 6px !important;
    box-sizing: border-box !important;
  }

  :global(#vfwidget .verbaflo-property-tab) {
    flex: 1 0 0 !important;
    min-width: 0 !important;
    height: 22px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 6px !important;
    border: none !important;
    background-color: transparent !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    line-height: 1.5 !important;
    color: #898989 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  :global(#vfwidget .verbaflo-property-tab.active) {
    background-color: #ffffff !important;
    color: #1c6cff !important;
    font-weight: 600 !important;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.08) !important;
  }

  :global(#vfwidget .verbaflo-property-tab-panel) {
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
    flex: 1 0 0 !important;
    min-height: 0 !important;
    width: 100% !important;
    overflow-y: auto !important;
  }

  :global(#vfwidget .verbaflo-property-tab-panel)::-webkit-scrollbar {
    width: 8px !important;
  }

  :global(#vfwidget .verbaflo-property-tab-panel)::-webkit-scrollbar-thumb {
    background-color: rgba(182, 184, 188, 0.7) !important;
    border-radius: 248px !important;
  }

  :global(#vfwidget .verbaflo-property-card-title-container) {
    color: #ffffff !important;
    position: absolute !important;
    bottom: 8px !important;
    left: 8px !important;
    z-index: 10 !important;
    max-width: 95% !important;
  }

  :global(#vfwidget .verbaflo-property-card-title) {
    font-weight: 400 !important;
    font-size: 16px !important;
    margin: 0 !important;
    word-wrap: break-word !important;
  }

  :global(#vfwidget .verbaflo-property-card-room-name-title) {
    font-weight: 500 !important;
    font-size: 12px !important;
    margin: 0 !important;
    word-wrap: break-word !important;
  }

  :global(#vfwidget .verbaflo-property-data-row) {
    display: flex !important;
    gap: 4px !important;
    align-items: baseline !important;
    margin: 0 !important;
    width: 100% !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-property-label) {
    font-weight: 600 !important;
    flex-shrink: 0 !important;
    font-size: 12px !important;
    line-height: 1.5 !important;
    color: #252b37 !important;
  }

  :global(#vfwidget .verbaflo-property-value) {
    font-weight: 400 !important;
    margin: 0 !important;
    flex: 1 0 0 !important;
    min-width: 0 !important;
    font-size: 12px !important;
    line-height: 1.5 !important;
    color: #252b37 !important;
    text-transform: capitalize !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* IDs render exactly as sent, not capitalized like other values. */
  :global(#vfwidget .verbaflo-property-value.verbaflo-property-value-raw) {
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-property-value-unit) {
    font-size: 12px !important;
    font-weight: 400 !important;
    line-height: 1.5 !important;
    text-transform: lowercase !important;
  }

  :global(#vfwidget .verbaflo-property-feature-column) {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 0 0 !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-feature-cell) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    height: 33px !important;
    padding: 8px 0 !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    border-bottom: 1px solid #e9eaeb !important;
  }

  :global(#vfwidget .verbaflo-property-feature-label) {
    flex: 1 0 0 !important;
    min-width: 0 !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    line-height: 1.5 !important;
    color: #252b37 !important;
    text-transform: capitalize !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  :global(#vfwidget .verbaflo-property-price-value) {
    font-weight: 700 !important;
    font-size: 16px !important;
    margin: 0 !important;
  }

  :global(#vfwidget .verbaflo-property-week-label) {
    font-weight: 400 !important;
    font-size: 12px !important;
    color: #637481 !important;
    letter-spacing: 0.8px !important;
    margin: 0 !important;
  }

  :global(#vfwidget .verbaflo-property-card-bottom-container) {
    display: flex !important;
    justify-content: space-between !important;
    margin-top: 16px !important;
    align-items: center !important;
  }

  :global(#vfwidget .verbaflo-property-card-button) {
    color: #ffffff !important;
    cursor: pointer !important;
    font-weight: 500 !important;
    font-size: 12px !important;
    width: fit-content !important;
    padding: 6px 12px !important;
    border-radius: 4px !important;
    border: none !important;
  }

  :global(#vfwidget .verbaflo-property-card-button:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-property-card-button:active) {
    text-decoration: none !important;
  }
</style>
